/**
 * F4.2 — Built-in scoring profile presets.
 *
 * Each preset represents a common relocation archetype. Weights reflect
 * what that profile cares about; descriptions map to i18n keys.
 */

import type { ProfileWeights, ScoringDimension } from './scoreComune';

export type ProfilePresetId =
  | 'balanced'
  | 'family'
  | 'nomad'
  | 'retiree'
  | 'mountain';

export interface ProfilePreset {
  id: ProfilePresetId;
  nameKey: string;           // i18n key
  descriptionKey: string;    // i18n key
  emoji: string;
  weights: ProfileWeights;
}

const W = (
  climate: number,
  cost: number,
  infra: number,
  foreign: number,
  tax: number,
): ProfileWeights => ({ climate, cost, infra, foreign, tax });

// Weights are deliberately well-separated across profiles so switching one
// visibly moves the overall score (and reorders the dimension bars in the
// DetailsPanel). Each profile leans hard on the 1–2 dimensions its archetype
// actually cares about; the others are damped, not zeroed.
export const PROFILE_PRESETS: ProfilePreset[] = [
  {
    id: 'balanced',
    nameKey: 'scoring.presets.balanced.name',
    descriptionKey: 'scoring.presets.balanced.description',
    emoji: '⚖️',
    // True neutral baseline — every dimension counts equally.
    weights: W(1, 1, 1, 1, 1),
  },
  {
    id: 'family',
    nameKey: 'scoring.presets.family.name',
    descriptionKey: 'scoring.presets.family.description',
    emoji: '👨‍👩‍👧',
    // Schools, hospitals, transport (infra) dominate; cost matters next.
    weights: W(0.8, 1.5, 3, 0.6, 0.6),
  },
  {
    id: 'nomad',
    nameKey: 'scoring.presets.nomad.name',
    descriptionKey: 'scoring.presets.nomad.description',
    emoji: '💻',
    // Climate + affordability + connectivity; tax-aware, community low.
    weights: W(2, 2, 1.5, 0.4, 1.2),
  },
  {
    id: 'retiree',
    nameKey: 'scoring.presets.retiree.name',
    descriptionKey: 'scoring.presets.retiree.description',
    emoji: '🌅',
    // Mild climate + healthcare access + favourable tax on pensions.
    weights: W(2.5, 1.2, 2, 0.3, 1.5),
  },
  {
    id: 'mountain',
    nameKey: 'scoring.presets.mountain.name',
    descriptionKey: 'scoring.presets.mountain.description',
    emoji: '🏔️',
    // Alpine climate is almost everything; the rest is secondary.
    weights: W(4, 0.7, 0.5, 0.3, 0.3),
  },
];

export const PROFILE_PRESETS_BY_ID = Object.fromEntries(
  PROFILE_PRESETS.map((p) => [p.id, p]),
) as Record<ProfilePresetId, ProfilePreset>;

export const DIMENSION_LABELS: Record<ScoringDimension, string> = {
  climate: 'scoring.dimensions.climate',
  cost: 'scoring.dimensions.cost',
  infra: 'scoring.dimensions.infra',
  foreign: 'scoring.dimensions.foreign',
  tax: 'scoring.dimensions.tax',
};
